/**
 * The limit upsell (owner request 2026-10-19: "show upselling when you plan posts and you won't
 * have enough limits"; design limit-upsell a). A flow that plans posts or video credits compares
 * what it needs with what is left BEFORE the click, and shows an inline card with the numbers and
 * the one step that helps: a monthly add-on (the owner of a web plan), the plans (Free, the trial,
 * a store plan), or a note to ask the owner (a member). Every card also offers "Plan only what
 * fits", so nothing blocks.
 *
 * The rule of the server (`consumeQuota`): a post takes its scheduled-post unit in the month of the
 * charge, which is the month when it gets its time (the click), not the month when it goes out. So
 * every post that a flow schedules now needs a unit of THIS month. A video credit is spent when the
 * reel or the asset is made, also now.
 *
 * Pure: the web app and the native apps can copy the rules. Only type imports.
 */
import {
  MONTHLY_ADD_ON_MONTHS_PER_YEAR,
  MONTHLY_ADD_ON_SLIDERS,
  monthlyAddOnCents,
  type MonthlyAddOnInterval,
  type MonthlyAddOnKind,
  type MonthlyAddOnQuantities,
} from './monthlyAddOns';
import { REELS_PLAN_MIN_DAYS, reelsPlanReelCount, type ReelsPlanCadence, type ReelsPlanPostsPerDay } from './reelsPlan';

/** What a flow is short of: the scheduled posts of this month, or the video credits. */
export type LimitUpsellKind = MonthlyAddOnKind;

export interface LimitShortfall {
  kind: LimitUpsellKind;
  /** What the flow needs now. */
  needed: number;
  /** What is left now (the plan left this month, plus the packs for credits). */
  left: number;
  /** `needed - left`, always 1 or more. */
  short: number;
}

/**
 * The shortfall of a flow, or null when it fits. A limit of null (no limit) always fits; so does a
 * missing or broken number (the server decides then). Pure.
 */
export function limitShortfall(
  kind: LimitUpsellKind,
  needed: number | null | undefined,
  left: number | null | undefined
): LimitShortfall | null {
  if (typeof needed !== 'number' || typeof left !== 'number' || !Number.isFinite(needed) || !Number.isFinite(left)) {
    return null;
  }
  const safeLeft = Math.max(0, Math.floor(left));
  const safeNeeded = Math.max(0, Math.ceil(needed));
  if (safeNeeded <= safeLeft) {
    return null;
  }
  return { kind, needed: safeNeeded, left: safeLeft, short: safeNeeded - safeLeft };
}

/**
 * The availability of `billing.monthlyAddOns` (music24-api `MonthlyAddOnAvailability`). A copy of
 * the union, so this file needs no server import.
 */
export type LimitUpsellAvailability =
  | 'available'
  | 'notOwner'
  | 'storeSubscription'
  | 'needsPlan'
  | 'noWebSubscription';

/**
 * The offer of the card (owner decisions of 2026-10-19):
 * - `addOn`: the owner of a paid web (Stripe) plan adds units to the monthly add-on.
 * - `plans`: Free and the trial get the plan offer, with the shortfall as the reason.
 * - `higherPlan`: a store plan (App Store, Google Play) or a plan with no web subscription gets the
 *   higher plans; add-ons are sold with web plans only.
 * - `askOwner`: a member asks the owner of the workspace.
 */
export type LimitUpsellOffer = 'addOn' | 'plans' | 'higherPlan' | 'askOwner';

/** The offer for an availability. Unknown or no answer: the plans (they always work). Pure. */
export function limitUpsellOffer(availability: LimitUpsellAvailability | null | undefined): LimitUpsellOffer {
  if (availability === 'available') {
    return 'addOn';
  }
  if (availability === 'notOwner') {
    return 'askOwner';
  }
  if (availability === 'storeSubscription' || availability === 'noWebSubscription') {
    return 'higherPlan';
  }
  return 'plans';
}

/**
 * The units to add for a shortfall: the shortfall rounded up to the slider step (10), and never
 * past the slider end. Null when the slider has no room left (the add-on is at its maximum). Pure.
 */
export function addOnUnitsFor(kind: LimitUpsellKind, short: number, current: number): number | null {
  const { step, max } = MONTHLY_ADD_ON_SLIDERS[kind];
  const room = Math.max(0, max - Math.max(0, current));
  if (room === 0 || short <= 0) {
    return null;
  }
  const rounded = Math.ceil(short / step) * step;
  return Math.min(rounded, room - (room % step));
}

export interface AddOnOffer {
  kind: LimitUpsellKind;
  /** The units that the click adds. */
  add: number;
  /** The quantities that `billing.setMonthlyAddOns` gets: the current ones plus `add` of the kind. */
  next: MonthlyAddOnQuantities;
  /** The extra price of `add` units for one billing period, in US cents (graduated tiers). */
  addPeriodCents: number;
  /** Every add-on after the click, for one billing period, in US cents. */
  addOnsPeriodCents: number;
  /** The plan, the extra artists and the add-ons after the click; null with no plan price. */
  totalPeriodCents: number | null;
  interval: MonthlyAddOnInterval;
}

/**
 * The add-on offer for a shortfall, or null when the slider has no room. The price of the extra
 * units is the difference of the graduated prices, so it equals the Stripe invoice. Pure.
 */
export function addOnOfferFor(input: {
  shortfall: LimitShortfall;
  current: MonthlyAddOnQuantities;
  interval: MonthlyAddOnInterval | null;
  /** `planPeriodCents` of `billing.monthlyAddOns`. */
  planPeriodCents: number | null;
}): AddOnOffer | null {
  const { shortfall, current, planPeriodCents } = input;
  const interval = input.interval ?? 'monthly';
  const { kind } = shortfall;
  const add = addOnUnitsFor(kind, shortfall.short, current[kind]);
  if (add === null || add <= 0) {
    return null;
  }
  const next: MonthlyAddOnQuantities = { ...current, [kind]: current[kind] + add };
  const months = interval === 'yearly' ? MONTHLY_ADD_ON_MONTHS_PER_YEAR : 1;
  const addMonthly = monthlyAddOnCents(kind, next[kind]) - monthlyAddOnCents(kind, current[kind]);
  const addOnsMonthly = monthlyAddOnCents('posts', next.posts) + monthlyAddOnCents('videoCredits', next.videoCredits);
  const addOnsPeriodCents = addOnsMonthly * months;
  return {
    kind,
    add,
    next,
    addPeriodCents: addMonthly * months,
    addOnsPeriodCents,
    totalPeriodCents: planPeriodCents === null ? null : planPeriodCents + addOnsPeriodCents,
    interval,
  };
}

/**
 * "Plan only what fits" of an Auto campaign: the longest length from `REELS_PLAN_MIN_DAYS` up to
 * one day less than `days` whose reels fit the credits left and whose posts (reels x channels) fit
 * the posts left. A null limit always fits. Null when not even the shortest plan fits. Pure.
 */
export function reelsPlanDaysThatFit(input: {
  days: number;
  cadence: ReelsPlanCadence;
  postsPerDay: ReelsPlanPostsPerDay;
  channels: number;
  /** Credits for one reel (1 today). */
  creditsPerReel: number;
  creditsLeft: number | null;
  postsLeft: number | null;
}): number | null {
  const { cadence, postsPerDay, channels, creditsPerReel, creditsLeft, postsLeft } = input;
  for (let days = input.days - 1; days >= REELS_PLAN_MIN_DAYS; days -= 1) {
    const reels = reelsPlanReelCount({ days, cadence, postsPerDay });
    const creditsFit = creditsLeft === null || reels * creditsPerReel <= creditsLeft;
    const postsFit = postsLeft === null || reels * channels <= postsLeft;
    if (creditsFit && postsFit) {
      return days;
    }
  }
  return null;
}
